import { useEffect, useRef, useMemo } from 'react';
import { CircleNotchIcon } from '@phosphor-icons/react';
import { useEmailThread } from '@/api/communications/email-threads/useEmailThread';
import { useActivityGraphContext } from '../ActivityGraphContext';
import { DocumentItem, DocumentItemData } from './DocumentItem';

interface DocumentsTabProps {
  conversationId?: string | null;
}

export function DocumentsTab({ conversationId }: DocumentsTabProps) {
  const context = useActivityGraphContext();
  const {
    data: threadData,
    isLoading,
    error,
  } = useEmailThread(conversationId ?? null);
  const documentRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const expandedDocumentId = context?.expandedDocumentId ?? null;
  const highlightedDocumentId = context?.highlightedDocumentId ?? null;

  const isInlineImageDocument = (document: {
    contentId?: string | null;
    mimeType?: string | null;
    originalFileName?: string | null;
    name?: string | null;
  }) => {
    if (!document.contentId) return false;
    if (document.mimeType?.startsWith('image/')) return true;

    const fileName = (
      document.originalFileName ||
      document.name ||
      ''
    ).toLowerCase();
    return (
      fileName.endsWith('.png') ||
      fileName.endsWith('.jpg') ||
      fileName.endsWith('.jpeg') ||
      fileName.endsWith('.gif') ||
      fileName.endsWith('.webp') ||
      fileName.endsWith('.svg')
    );
  };

  // Aggregate all documents from all emails in the thread
  const allDocuments = useMemo(() => {
    if (!threadData?.emails) return [];

    const documents: DocumentItemData[] = [];

    for (const email of threadData.emails) {
      // Add document associations
      if (email.documentAssociations) {
        for (const assoc of email.documentAssociations) {
          if (isInlineImageDocument(assoc.document)) continue;

          documents.push({
            id: assoc.id,
            documentId: assoc.document.id,
            fileName: assoc.document.originalFileName || assoc.document.name,
            sizeBytes: assoc.document.fileSizeBytes || 0,
            mimeType: assoc.document.mimeType,
            s3Url: assoc.document.url,
            sourceEmailSubject: email.subject ?? undefined,
          });
        }
      }

      // Add legacy attachments
      if (email.attachments) {
        for (const att of email.attachments) {
          documents.push({
            id: att.id,
            fileName: att.fileName,
            sizeBytes: att.sizeBytes,
            mimeType: att.mimeType,
            s3Url: att.s3Url,
            sourceEmailSubject: email.subject ?? undefined,
          });
        }
      }
    }

    return documents;
  }, [threadData?.emails]);

  // Scroll to highlighted document
  useEffect(() => {
    if (highlightedDocumentId) {
      // Find the document by either documentId or id
      const docKey =
        allDocuments.find(
          (d) =>
            d.documentId === highlightedDocumentId ||
            d.id === highlightedDocumentId,
        )?.documentId ||
        allDocuments.find((d) => d.id === highlightedDocumentId)?.id;

      if (docKey && documentRefs.current.has(docKey)) {
        const element = documentRefs.current.get(docKey);
        element?.scrollIntoView({ behavior: 'smooth', block: 'center' });

        const timer = setTimeout(() => {
          context?.clearHighlights();
        }, 2000);

        return () => clearTimeout(timer);
      }
    }
  }, [highlightedDocumentId, allDocuments, context]);

  const handleToggleExpanded = (documentId: string) => {
    context?.toggleDocumentExpanded(documentId);
  };

  if (!conversationId) {
    return (
      <div className="flex items-center justify-center p-8 text-muted-foreground">
        No documents available
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <CircleNotchIcon className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center p-8 text-destructive">
        Error loading documents
      </div>
    );
  }

  if (allDocuments.length === 0) {
    return (
      <div className="flex items-center justify-center p-8 text-muted-foreground">
        No documents attached to this conversation
      </div>
    );
  }

  return (
    <div className="p-3 space-y-2">
      <div className="text-sm text-muted-foreground mb-3">
        {allDocuments.length} document{allDocuments.length !== 1 ? 's' : ''}
      </div>
      {allDocuments.map((doc) => {
        const docKey = doc.documentId || doc.id;
        const isExpanded = expandedDocumentId === docKey;
        const isHighlighted = highlightedDocumentId === docKey;

        return (
          <div
            key={doc.id}
            ref={(el) => {
              if (el) documentRefs.current.set(docKey, el);
            }}
          >
            <DocumentItem
              document={doc}
              isExpanded={isExpanded}
              isHighlighted={isHighlighted}
              onToggleExpanded={() => handleToggleExpanded(docKey)}
            />
          </div>
        );
      })}
    </div>
  );
}
